import axios from "axios";
import { CheckToken } from "../../login/AccessToken";

/**
 * Bed / Bed Type master APIs — backing the Bed Management and Bed Type
 * Management screens.
 *
 *   /ipd_bed/             GET list | POST create | DELETE
 *   /ipd_bed/update/      POST update
 *   /ipd_bedtype/         GET list | POST create | DELETE
 *   /ipd_bedtype/update/  POST update
 *
 * Both updates POST to an `update/` sub-path, with the `code` in the body as
 * the identifier.
 *
 * Both live on the main Django backend, so they use REACT_APP_BACKEND_ROUTE_URL
 * (the same base AdtBedService and bedCensusApi already read them from).
 * Responses follow the usual { status, data | message } envelope.
 */

const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;

// Bearer is not attached globally (see Axios-interceptor), so every call
// attaches it here after CheckToken() pre-refreshes an expired JWT.
function authHeaders() {
  CheckToken();
  return {
    Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
  };
}

function listOf(body) {
  return Array.isArray(body?.data) ? body.data : [];
}

// ─── Bed Type ─────────────────────────────────────────────────────────────

export async function getBedTypes() {
  const res = await axios.get(`${BASE}ipd_bedtype/`, { headers: authHeaders() });
  return listOf(res?.data);
}

export async function createBedType(payload) {
  const res = await axios.post(`${BASE}ipd_bedtype/`, payload, {
    headers: authHeaders(),
  });
  return res?.data;
}

// POST ipd_bedtype/update/ — partial update: only the keys present are changed.
// `code` identifies the bed type and is not editable.
export async function updateBedType(payload) {
  const res = await axios.post(`${BASE}ipd_bedtype/update/`, payload, {
    headers: authHeaders(),
  });
  return res?.data;
}

export async function deleteBedType(code) {
  const res = await axios.delete(`${BASE}ipd_bedtype/`, {
    headers: authHeaders(),
    data: { code },
  });
  return res?.data;
}

// ─── Bed ──────────────────────────────────────────────────────────────────

export async function getBeds() {
  const res = await axios.get(`${BASE}ipd_bed/`, { headers: authHeaders() });
  return listOf(res?.data);
}

export async function createBed(payload) {
  const res = await axios.post(`${BASE}ipd_bed/`, payload, {
    headers: authHeaders(),
  });
  return res?.data;
}

// POST ipd_bed/update/ — partial update: only the keys present are changed.
// `code` identifies the bed and is not editable.
export async function updateBed(payload) {
  const res = await axios.post(`${BASE}ipd_bed/update/`, payload, {
    headers: authHeaders(),
  });
  return res?.data;
}

export async function deleteBed(code) {
  const res = await axios.delete(`${BASE}ipd_bed/`, {
    headers: authHeaders(),
    data: { code },
  });
  return res?.data;
}

// ─── Departments (for the bed form's department_id) ───────────────────────

export async function getMedicalDepartments() {
  const res = await axios.get(`${BASE}get_all_medical_departments/`, {
    headers: authHeaders(),
  });
  const list = res?.data?.department_list;
  return Array.isArray(list) ? list : [];
}
